import {useCallback} from 'react';

import {trackEvent} from 'src/modules/mixpanel';
import {trackUserEvents} from 'src/openapi/admin/apis/TrackUserEventApi';
import {UserEventCode} from 'src/openapi/admin/models/UserEventCode';
import {useAppDispatch} from 'src/store/hooks';

export const useEventTracking = () => {
    const appDispatch = useAppDispatch();

    const attributeCopy = useCallback(({attribute}: {attribute: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {userEvent: {eventCode: UserEventCode.CP_ATTR_COPY, attribute}},
        }));
        trackEvent('CP_ATTR_COPY', {attribute});
    }, [appDispatch]);

    const configStatusSelect = useCallback(() => {
        appDispatch(trackUserEvents({
            userEventRequest: {userEvent: {eventCode: UserEventCode.CP_CFG_STATUS_SEL}},
        }));
        trackEvent('CP_CFG_STATUS_SEL');
    }, [appDispatch]);

    const copyConfigurationValue = useCallback(({field}: {field: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {userEvent: {eventCode: UserEventCode.CP_COPY_CONF_VAL, field}},
        }));
        trackEvent('CP_COPY_CONF_VAL', {field});
    }, [appDispatch]);

    const domainsAdd = useCallback(({domain, domainCount}: {domain: string, domainCount: number}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {userEvent: {eventCode: UserEventCode.CP_DOMAINS_ADD, domain, domainCount}},
        }));
        trackEvent('CP_DOMAINS_ADD', {domain, domainCount});
    }, [appDispatch]);

    const domainVerificationComplete = useCallback(({domain, isPrimary, valid}: {
        domain: string,
        isPrimary: boolean,
        valid: boolean,
    }) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.CP_DOM_VERIF_COMPLETE, domain, isPrimary, valid},
            },
        }));
        trackEvent('CP_DOM_VERIF_COMPLETE', {domain, isPrimary, valid});
    }, [appDispatch]);

    const domainVerificationStart = useCallback(({domain}: {domain: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {userEvent: {eventCode: UserEventCode.CP_DOM_VERIF_START, domain}},
        }));
        trackEvent('CP_DOM_VERIF_START', {domain});
    }, [appDispatch]);

    const downloadXml = useCallback(() => {
        appDispatch(trackUserEvents({
            userEventRequest: {userEvent: {eventCode: UserEventCode.CP_DWN_XML}},
        }));
        trackEvent('CP_DWN_XML');
    }, [appDispatch]);

    const homeView = useCallback(({userRole}: {userRole: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {userEvent: {eventCode: UserEventCode.CP_HOME_VIEW, userRole}},
        }));
        trackEvent('CP_HOME_VIEW', {userRole});
    }, [appDispatch]);

    const idpCardSelect = useCallback(({ssoIdpSelected}: {ssoIdpSelected: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {userEvent: {eventCode: UserEventCode.CP_IDP_CARD_SEL, ssoIdpSelected}},
        }));
        trackEvent('CP_IDP_CARD_SEL', {ssoIdpSelected});
    }, [appDispatch]);

    const idpChange = useCallback(({fromIdp, toIdp}: {fromIdp: string, toIdp: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {userEvent: {eventCode: UserEventCode.CP_IDP_CHANGE, fromIdp, toIdp}},
        }));
        trackEvent('CP_IDP_CHANGE', {fromIdp, toIdp});
    }, [appDispatch]);

    const metadataSelect = useCallback(({method}: {method: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {userEvent: {eventCode: UserEventCode.CP_META_SEL, method}},
        }));
        trackEvent('CP_META_SEL', {method});
    }, [appDispatch]);

    const metadataValidate = useCallback(({method, valid}: {method: string, valid: boolean}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {userEvent: {eventCode: UserEventCode.CP_META_VALIDATE, method, valid}},
        }));
        trackEvent('CP_META_VALIDATE', {method, valid});
    }, [appDispatch]);

    const requiredCompanyLoginToggle = useCallback(({action, domain, idpProvider}: {
        action: string,
        domain: string,
        idpProvider: string,
    }) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.CP_REQ_CO_LOGIN_TOGGLE, action, domain, idpProvider},
            },
        }));
        trackEvent('CP_REQ_CO_LOGIN_TOGGLE', {action, domain, idpProvider});
    }, [appDispatch]);

    const ssoConfirmCheck = useCallback(() => {
        appDispatch(trackUserEvents({
            userEventRequest: {userEvent: {eventCode: UserEventCode.CP_SSO_CONFIRM_CHK}},
        }));
        trackEvent('CP_SSO_CONFIRM_CHK');
    }, [appDispatch]);

    const ssoWizardStepOpen = useCallback(({currentStepName, nextStepName, previousStepName}: {
        currentStepName: string,
        nextStepName: string,
        previousStepName: string,
    }) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {
                    eventCode: UserEventCode.CP_SSO_WIZ_STEP_OPEN,
                    currentStepName,
                    nextStepName,
                    previousStepName,
                },
            },
        }));
        trackEvent('CP_SSO_WIZ_STEP_OPEN', {currentStepName, nextStepName, previousStepName});
    }, [appDispatch]);

    return {
        attributeCopy,
        configStatusSelect,
        copyConfigurationValue,
        domainsAdd,
        domainVerificationComplete,
        domainVerificationStart,
        downloadXml,
        homeView,
        idpCardSelect,
        idpChange,
        metadataSelect,
        metadataValidate,
        requiredCompanyLoginToggle,
        ssoConfirmCheck,
        ssoWizardStepOpen,
    };
};
